import {ChatRoomInfo, ChatRoomInfoMember, Employee, Team} from "../../api";
import React, {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {ConfigInterface} from "../../config";
import {useAuth} from "../../auth/hook";
import {ChatConversation} from "../types/chat-conversation";
import {ChatUser} from "../types/chat-user";
import {ChatMessage} from "../types/chat-message";
import {useChatRoomsInfo} from "../hooks/use-chat-rooms-info";
import {ChatContextType} from "../types/chat-context";
import {useRocketChat} from "../rocketchat/use-rocket-chat";
import {useChatMetadataContext} from "../hooks/use-chat-metadata-context";


export const ChatContext = React.createContext<ChatContextType>({} as ChatContextType)

export function ChatContextProvider({ children, rocketChatWebsocketUrl, config, employees, teams }:{children: React.ReactNode, config: ConfigInterface, rocketChatWebsocketUrl: string, employees: Employee[], teams: Team[]}): ReactNode {
  const [currentConversationId, setCurrentConversationId] = React.useState<string|null>(null)
  const { token, user, hasRoles } = useAuth()
  const {getMetadata} = useChatMetadataContext()

  /**
   * Browser notifications
   */
  const [isTabFocussed, setIsTabFocussed] = useState(true)
  const [hasNotificationPermissions, setHasNotificationPermissions] = useState(false)
  useEffect(() => {
    if (window) {
      window.addEventListener('visibilitychange', (e) => {
        const doc = e.target as {hidden: boolean}|null
        setIsTabFocussed(doc?.hidden === false)
      })
      if ('Notification' in window) {
        if (Notification.permission !== 'denied' && Notification.permission !== 'granted') {
          Notification.requestPermission().then((result) => {
            if (result === 'granted') {
              setHasNotificationPermissions(true)
            }
          })
        } else {
          setHasNotificationPermissions(Notification.permission === 'granted')
        }
      }
    }
  }, [])
  const onNewMessage = useCallback((message: ChatMessage) => {
    const isMuted = getMetadata(message.conversationId)?.is_muted
    if (!isMuted && hasNotificationPermissions && !isTabFocussed && message.userId !== user?.sub) {
      new Notification('Nieuw chatbericht in Miep', {body: message.message})
    }
  }, [hasNotificationPermissions, isTabFocussed, user, getMetadata])

  const { conversations: rawConversations, messages, sendAttachment, sendChatMessage, connected, loaded, refreshRooms, loadMoreHistory, hasUnloadedHistory, deleteMessage, formatMediaUrl } = useRocketChat(rocketChatWebsocketUrl, config.rocketChatAuthUrl, user, token, onNewMessage)
  const { chatRooms: chatRoomsFromApi } = useChatRoomsInfo(config)
  const [chatRooms, setChatRooms] = useState<ChatRoomInfo[]>([])
  useEffect(() => {
    if (chatRooms.length === 0 && chatRoomsFromApi.length > 0) {
      setChatRooms(chatRoomsFromApi)
    }
  }, [chatRoomsFromApi, chatRooms])
  const canAddChats = useMemo(() => hasRoles(['manager', 'coordinator']), [hasRoles])
  const conversations = useMemo<(ChatConversation&{description?: string|null})[]>(() => {
    const chatRoomIds = chatRooms.map(r => r.id)
    return rawConversations.filter(c => chatRoomIds.includes(c.id))
      .map((c: ChatConversation&{description?: string|null|undefined}) => {
        const room = chatRooms.find(r => r.id === c.id)
        if (room!.type === "network") {
          c.name = "Mijn netwerk"
        } else {
          c.name = room!.name ?? `Fout: ${room!.id}`
        }
        c.purgedAt = room?.purged_at ?? null
        c.teamId = room?.team_id ? room.team_id.toString() : null
        c.members = room?.members ?? []
        if (room?.type === 'message') {
          if (Number.isSafeInteger(room.concerns['ons_employee_id'])) {
            const employee = employees.find(e => e.id === room.concerns['ons_employee_id'])
            const team = employee?.team_id ? teams.find(t => t.id === employee.team_id) : null
            if (team) {
              c.description = team.name
            }
          }
        }
        return c
      })
  }, [rawConversations, chatRooms, employees, teams])

  const chat = useMemo(() => {
    const conversation = conversations.find(c => c.id === currentConversationId)
    const chatRoom = chatRooms.find(r => r.id === currentConversationId)
    if (! chatRoom|| !conversation) {
      return;
    }
    return {
      ...chatRoom,
      ...conversation,
    }
  }, [currentConversationId,conversations,chatRooms])

  const createAvatarUrl = useCallback((member: ChatRoomInfoMember): string|null => {
    return member.employee_picture_url === null
      ? null
      :`${member.employee_picture_url}&accessToken=${token}`
  }, [token])

  const users = useMemo<ChatUser[]>(() => {
    const userMap: {[sub: string]: ChatUser} = {}
    chatRooms.forEach(r => {
      r.members.forEach((m) => {
        userMap[m.sub] = {
          id: m.sub,
          name: m.name,
          role: m.role,
          avatar: createAvatarUrl(m),
        }
      })
    })
    return Object.values(userMap)
  }, [chatRooms, createAvatarUrl])

  const currentChatMembers: ChatRoomInfoMember[] = useMemo(() => {
    const currentConversation = chatRooms.find(c => c.id === currentConversationId)
    return (currentConversation?.members || []).map(m => {
      return {
        ...m,
        employee_picture_url: createAvatarUrl(m),
      }
    })
  }, [chatRooms, users, currentConversationId, createAvatarUrl])

  useEffect(() => {
    if (conversations.length > 0 && currentConversationId === null) {
      setCurrentConversationId(conversations[0].id)
    }
  }, [currentConversationId, setCurrentConversationId, conversations])


  return <ChatContext.Provider value={{
    connected,
    loaded,
    conversations,
    users,
    chat,
    messages,
    currentConversationId,
    currentChatMembers,
    formatMediaUrl,
    selectConversation: (conversationId: string) => setCurrentConversationId(conversationId),
    sendMessage: sendChatMessage,
    canAddChats,
    employees,
    teams,
    loadMoreHistory,
    hasUnloadedHistory,
    loadChatRooms: rooms => {
      setChatRooms(rooms)
      refreshRooms()
    },
    sendAttachment,
    deleteMessage,
  }}>
    {children}
  </ChatContext.Provider>
}
